import type { InferRequestType, InferResponseType } from 'hono/client';
import { useCallback } from 'react';
import { useSWRConfig } from 'swr';
import { type ApiClient, type ReadToastOptions, useFetch, useMutation } from '../api';

type GetDeviceNetworkConfigs =
  ApiClient['v2']['workspaces'][':workspaceId']['device-network-configs']['$get'];
export type DeviceNetworkConfigListResponse = InferResponseType<GetDeviceNetworkConfigs, 200>;
export type DeviceNetworkConfigResponse =
  DeviceNetworkConfigListResponse['deviceNetworkConfigs'][number];

type PostAssignDeviceNetworkConfigs =
  ApiClient['v2']['workspaces'][':workspaceId']['device-network-configs']['assign']['$post'];
export type BatchAssignDeviceNetworkConfigsRequest =
  InferRequestType<PostAssignDeviceNetworkConfigs>['json'];
export type BatchDeviceNetworkConfigAssignmentResponse = InferResponseType<
  PostAssignDeviceNetworkConfigs,
  200
>;

type PostUnassignDeviceNetworkConfigs =
  ApiClient['v2']['workspaces'][':workspaceId']['device-network-configs']['unassign']['$post'];
export type BatchUnassignDeviceNetworkConfigsRequest =
  InferRequestType<PostUnassignDeviceNetworkConfigs>['json'];

const getListCacheKey = (workspaceId: number, deviceId: number) =>
  `workspaces/${workspaceId}/device-network-configs?deviceId=${deviceId}`;

export function useDeviceNetworkConfigs(
  workspaceId: number,
  deviceId: number,
  toastOptions?: ReadToastOptions
) {
  return useFetch<DeviceNetworkConfigListResponse>(
    getListCacheKey(workspaceId, deviceId),
    (client) =>
      client.v2.workspaces[':workspaceId']['device-network-configs'].$get({
        param: { workspaceId: workspaceId.toString() },
        query: { deviceId: deviceId.toString() },
      }),
    toastOptions
  );
}

export function useAssignDeviceNetworkConfigs(
  workspaceId: number,
  deviceId: number,
  actionOnErr: () => void = () => {}
) {
  const { mutate } = useSWRConfig();
  const cacheKey = getListCacheKey(workspaceId, deviceId);

  const assignMutation = useMutation<
    BatchDeviceNetworkConfigAssignmentResponse,
    BatchAssignDeviceNetworkConfigsRequest
  >(
    `${cacheKey}/assign`,
    (client, request) =>
      client.v2.workspaces[':workspaceId']['device-network-configs'].assign.$post({
        param: { workspaceId: workspaceId.toString() },
        json: request,
      }),
    actionOnErr
  );

  return useCallback(
    async (request: BatchAssignDeviceNetworkConfigsRequest) => {
      const result = await assignMutation(request);
      await mutate(cacheKey);
      return result;
    },
    [assignMutation, mutate, cacheKey]
  );
}

export function useUnassignDeviceNetworkConfigs(
  workspaceId: number,
  deviceId: number,
  actionOnErr: () => void = () => {}
) {
  const { mutate } = useSWRConfig();
  const cacheKey = getListCacheKey(workspaceId, deviceId);

  const unassignMutation = useMutation<
    BatchDeviceNetworkConfigAssignmentResponse,
    BatchUnassignDeviceNetworkConfigsRequest
  >(
    `${cacheKey}/unassign`,
    (client, request) =>
      client.v2.workspaces[':workspaceId']['device-network-configs'].unassign.$post({
        param: { workspaceId: workspaceId.toString() },
        json: request,
      }),
    actionOnErr
  );

  return useCallback(
    async (request: BatchUnassignDeviceNetworkConfigsRequest) => {
      const result = await unassignMutation(request);
      await mutate(cacheKey);
      return result;
    },
    [unassignMutation, mutate, cacheKey]
  );
}
